import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// ── Normaliza roles legacy al nuevo conjunto ──────────────────────────
const LEGACY_ROLE_MAP = {
  gerencia: 'admin',
  intendente: 'admin',
  coach: 'scout',
  administracion: 'admin',
  user: 'admin',
};

const normalizeRole = (role) => {
  if (!role) return null;
  return LEGACY_ROLE_MAP[role] || role;
};

// ── Matriz de permisos por rol (4 roles) ──────────────────────────────
const RolePermissions = {
  // SuperAdmin: Acceso total + gestión de usuarios
  superadmin: {
    canManageUsers: true,
    canViewHistory: true,
    canManageFinance: true,
    canManageMaterials: true,
    canSubtractMaterials: true,
    canManageCategories: true,
    canManagePlayers: true,
    canValidatePlayers: true,
    canViewDashboard: true,
    canViewMap: true,
    canManageContracts: true,
    canManageTracking: true,
  },
  // Admin: Todo excepto gestión de usuarios
  admin: {
    canManageUsers: false,
    canViewHistory: true,
    canManageFinance: true,
    canManageMaterials: true,
    canSubtractMaterials: true,
    canManageCategories: true,
    canManagePlayers: true,
    canValidatePlayers: true,
    canViewDashboard: true,
    canViewMap: true,
    canManageContracts: true,
    canManageTracking: true,
  },
  // Legales: Contratos y fichas de jugadores (read-only en el resto)
  legales: {
    canManageUsers: false,
    canViewHistory: false,
    canManageFinance: false,
    canManageMaterials: false,
    canSubtractMaterials: false,
    canManageCategories: false,
    canManagePlayers: true, // ver ficha + editar campos de contratos
    canValidatePlayers: false,
    canViewDashboard: true,
    canViewMap: false,
    canManageContracts: true,
    canManageTracking: false,
  },
  // Scout: Visitas de seguimiento, crear/editar jugadores, solicitar materiales
  scout: {
    canManageUsers: false,
    canViewHistory: false,
    canManageFinance: false,
    canManageMaterials: false,
    canSubtractMaterials: true, // puede restar (entregar materiales)
    canManageCategories: false,
    canManagePlayers: true,
    canValidatePlayers: false,
    canViewDashboard: false,
    canViewMap: true,
    canManageContracts: false,
    canManageTracking: true,
  },
};

const EMPTY_PERMS = Object.keys(RolePermissions.scout).reduce((acc, k) => {
  acc[k] = false;
  return acc;
}, {});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const role = normalizeRole(user?.role);
  const permissions = role ? (RolePermissions[role] || EMPTY_PERMS) : EMPTY_PERMS;

  const value = {
    user,
    loading,
    login,
    logout,
    role,
    rawRole: user?.role,
    token: localStorage.getItem('token'),
    // Roles booleanos
    isSuperAdmin: role === 'superadmin',
    isAdmin: role === 'admin' || role === 'superadmin',
    isLegales: role === 'legales',
    isScout: role === 'scout',
    // Permisos
    permissions,
    ...permissions,
    assignedCategories: user?.assigned_categories || [],
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
